/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus';

import { isExpectedError } from '../../utils/isExpectedError';
import type { OnboardingKeysCombinedType } from '../../constants/onboarding-keys-combined';
import { expVal } from '@confluence/feature-experiments';

import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation';
import type {
	onboardingStateGetOnboardingStateQuery as onboardingStateGetOnboardingStateQuery$data,
	onboardingStateGetOnboardingStateQueryVariables as onboardingStateGetOnboardingStateQuery$variables,
} from './__types__/onboardingStateGetOnboardingStateQuery';

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?: readonly { key: string; value: string | null | undefined }[] | null;
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
) => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		onboardingState.forEach(({ key, value }) => {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				return;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		});
	}
	return state;
};

export const useSetOnboardingState = () => {
	const [setState, { error, called }] = useMutation<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		gql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(cache, { data }) {
				const states = data!.setOnboardingState;
				states.forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					cache.writeFragment({
						id: `OnboardingState:${key}`,
						fragment: gql`
							fragment myKey on OnboardingState {
								key
								value
							}
						`,
						data: {
							key,
							value,
							__typename: 'OnboardingState',
						},
					});
				});
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs) => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

// TODO: Migrate to fetch from userPreferences - this is considered deprecated
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
) => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	const { data, error, loading, refetch } = useQuery<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(
		expVal('confluence_consolidate_onboarding_state_queries', 'isEnabled', false)
			? // eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Preloaded GraphQL Operation
				gql`
					query GetOnboardingState($key: [String]) {
						onboardingState(key: $key) {
							key
							value
						}
					}
				`
			: gql`
					query onboardingStateGetOnboardingStateQuery($key: [String]) {
						onboardingState(key: $key) {
							key
							value
						}
					}
				`,
		{
			variables: { key },
			skip: skipQuery,
		},
	);

	return {
		data,
		error,
		loading,
		refetch,
	};
};
